import { IRecord } from "../types/schema"
import {
  companyFlowVisibilitySettingsFilter,
  flowVisibilitySettingsFilter,
} from "./companyFlowVisibilitySettingsFilter"
import { andSkeemFilter } from "./skeem"

export type IProbationFlow = IRecord<"flows", "name" | "prefix">

type ProbationScopeContext = {
  permissions: { canManageEmployees: boolean }
  accessibleCompanyIds: string[]
}

/** Employees visible on the probations page and sidebar count. Managers see all; others only direct reports. */
export function probationEmployeeScopeFilter(ctx: ProbationScopeContext) {
  const companyFilter = { matches: { company: { or: ctx.accessibleCompanyIds } } }
  if (ctx.permissions.canManageEmployees) {
    return companyFilter
  }
  return andSkeemFilter(companyFilter, { matches: { reportsTo: { session: true } } })
}

export function probationSidebarCountFilter(ctx: ProbationScopeContext, thirtyDaysFromNow: Date) {
  return andSkeemFilter(
    probationEmployeeScopeFilter(ctx),
    { matches: { probationDate: { lte: thirtyDaysFromNow } } },
    { not: { matches: { probationResult: { or: ["pass", "fail"] } } } },
  )
}

export function probationIndexEmployeeFilter(ctx: ProbationScopeContext) {
  return andSkeemFilter(probationEmployeeScopeFilter(ctx), {
    not: { matches: { probationDate: null } },
  })
}

export async function userCanAccessProbations(ctx: ProbationScopeContext): Promise<boolean> {
  if (ctx.permissions.canManageEmployees) return true
  const count = await window.skeem.fetch2<number>({
    count: { name: "employees", filter: probationEmployeeScopeFilter(ctx) },
  })
  return count > 0
}

/**
 * Filter for the probation flow visible to a company. Use with `first` + sort by name — first match wins.
 * `visibilityCompanyId` is the head company when the target company inherits flows.
 */
export function probationFlowFilter(companyId: string, visibilityCompanyId: string = companyId) {
  return andSkeemFilter(
    { matches: { isProbation: true, archived: false } },
    flowVisibilitySettingsFilter(companyId),
    companyFlowVisibilitySettingsFilter(visibilityCompanyId),
  )
}

function visibilityCompanyIdForFlows(
  company:
    | {
        inheritFlows?: boolean
        headCompany?: { id: string } | null
      }
    | null
    | undefined,
  companyId: string,
) {
  return company?.inheritFlows && company.headCompany?.id ? company.headCompany.id : companyId
}

/** Flow results tied to any probation flow (supports multiple flows across companies). */
export const probationFlowResultFilter = {
  matches: { flow: { matches: { isProbation: true, archived: false } } },
}

export function isActiveProbationCaseStatus(status: string) {
  return status !== "complete"
}

export async function fetchProbationFlowResultsForEmployee(employeeId: string) {
  return window.skeem.fetch2<{ id: string; status: string }[]>({
    model: {
      name: "flowResults",
      attributes: ["status"],
      filter: {
        and: [
          probationFlowResultFilter,
          {
            exists: {
              path: [{ name: "employees", filter: { matches: { id: employeeId } } }],
            },
          },
        ],
      },
    },
  })
}

export async function fetchEmployeeIdsWithActiveProbationCase(employeeIds: string[]) {
  if (employeeIds.length === 0) return new Set<string>()
  const results = await window.skeem.fetch2<{ status: string; employees: { id: string }[] }[]>({
    model: {
      name: "flowResults",
      attributes: ["status", { name: "employees", attributes: [] }],
      filter: {
        and: [
          probationFlowResultFilter,
          {
            exists: {
              path: [{ name: "employees", filter: { matches: { id: { or: employeeIds } } } }],
            },
          },
        ],
      },
    },
  })
  const activeIds = new Set<string>()
  for (const result of results) {
    if (!isActiveProbationCaseStatus(result.status)) continue
    for (const employee of result.employees) {
      activeIds.add(employee.id)
    }
  }
  return activeIds
}

async function fetchProbationFlowForCompanyWithVisibility(
  companyId: string,
  visibilityCompanyId: string,
): Promise<IProbationFlow | null> {
  return window.skeem.fetch2<IProbationFlow | null>({
    first: {
      name: "flows",
      attributes: ["name", "prefix"],
      filter: probationFlowFilter(companyId, visibilityCompanyId),
      sort: { by: "name", direction: "ASC" },
    },
  })
}

export async function fetchProbationFlowForCompany(
  companyId: string,
): Promise<IProbationFlow | null> {
  const byCompany = await fetchProbationFlowByCompanyId([companyId])
  return byCompany.get(companyId) ?? null
}

export async function fetchProbationFlowByCompanyId(
  companyIds: string[],
): Promise<Map<string, IProbationFlow | null>> {
  const uniqueIds = Array.from(new Set(companyIds.filter(Boolean)))
  if (uniqueIds.length === 0) return new Map()

  const companies = await window.skeem.fetch2<
    { id: string; inheritFlows: boolean; headCompany: { id: string } | null }[]
  >({
    model: {
      name: "companies",
      attributes: ["inheritFlows", { name: "headCompany", attributes: [] }],
      filter: { matches: { id: { or: uniqueIds } } },
    },
  })
  const companyById = new Map(companies.map(company => [company.id, company]))

  const entries = await Promise.all(
    uniqueIds.map(async companyId => {
      const flow = await fetchProbationFlowForCompanyWithVisibility(
        companyId,
        visibilityCompanyIdForFlows(companyById.get(companyId), companyId),
      )
      return [companyId, flow] as const
    }),
  )
  return new Map(entries)
}
